import React, { FC, memo, useMemo } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { ISubcard } from '../../cards/multicards.interface';
import { CardMarkupViewer } from '../../markup/cardMarkupViewer/cardMarkupViewer';
import { CardMarkupViewerState } from '../../markup/cardMarkupViewer/cardMarkupViewer.interface';
import { BlockMarkupViewer } from '../../markup/blockMarkupViewer/blockMarkupViewer';
import { ReviewState } from '../review.interface';
import { getSubcardMarkup } from '../utils/multicard.utils';
import { markupFormatter, subcardKey } from '../../common';
import { RootState } from '../../store/rootReducer';
import { ThemeProps } from '../../styles/theme';

const GroupBadge = styled.div<ThemeProps>`
    display: inline-block;
    align-self: flex-start;
    margin-bottom: 12px;
    padding: 3px 10px;
    border-radius: 10px;
    font-size: 11px;
    font-weight: 600;
    color: ${props => props.theme.buttonText};
    background-color: ${props => props.theme.accentColor};
`;

const MarkedBadge = styled.div<ThemeProps>`
    align-self: flex-start;
    margin-bottom: 8px;
    font-size: 0.85em;
    color: ${props => props.theme.secondaryText};
`;

const ParentQuestion = styled.div<ThemeProps>`
    width: 100%;
    margin-top: 16px;
    padding-top: 10px;
    border-top: 1px solid ${props => props.theme.primaryBorder};
    font-size: 0.9em;
    opacity: 0.8;
`;

interface SubcardViewProps {
    cardId?: string;
    currentSubcard?: ISubcard;
    reviewState: ReviewState;
    markupState?: CardMarkupViewerState;
    // Full card markup, used to surface the card-level {reference} block while a
    // single subcard is shown.
    cardMarkup?: string;
}

// Removed pagination dots and progress indicator

export const SubcardView: FC<SubcardViewProps> = memo(({
    cardId,
    currentSubcard,
    reviewState,
    markupState,
    cardMarkup
}) => {
    const markupToShow = useMemo(() => {
        if (!currentSubcard) return '';
        return getSubcardMarkup(currentSubcard, reviewState);
    }, [currentSubcard, reviewState]);

    const parentCard = useMemo(
        () => (cardMarkup !== undefined && cardMarkup.length > 0
            ? markupFormatter.parse(cardMarkup)
            : undefined),
        [cardMarkup]
    );
    const reference = parentCard?.reference ?? [];

    const subcardMark = useSelector((state: RootState) => {
        if (cardId === undefined || currentSubcard === undefined) return undefined;
        const key = subcardKey(currentSubcard.question, currentSubcard.answer);
        return state.cards[cardId]?.mark?.subcards.find(mark => mark.subcardKey === key);
    });

    if (!currentSubcard) {
        return null;
    }

    return (
        <>
            {currentSubcard.group !== undefined && currentSubcard.group.length > 0 && (
                <GroupBadge>{currentSubcard.group}</GroupBadge>
            )}
            {subcardMark !== undefined && (
                <MarkedBadge data-testid="subcard-marked">
                    🚩 Marked{subcardMark.note !== '' ? `: ${subcardMark.note}` : ''}
                </MarkedBadge>
            )}
            <CardMarkupViewer
                cardId={cardId}
                markup={markupToShow}
                state={markupState!}
                referenceOverride={reference}
                expandReference={false}
            />
            {reviewState === ReviewState.SubcardQuestion && parentCard !== undefined && parentCard.question.length > 0 && (
                <ParentQuestion data-testid="subcard-parent-question">
                    <BlockMarkupViewer cardId={cardId} blockMarkup={parentCard.question}/>
                </ParentQuestion>
            )}
        </>
    );
});
